/**
 * useUncategorizedTransactions — single paged fetch for ALL uncategorized rows.
 *
 * This shared hook is the single source of truth for the uncategorized
 * transaction list that both `usePropagateEntity` and `AutoCategorizeDialog`
 * need. Before this existed, each consumer registered the same React Query key
 * (`["transactions","uncategorized-for-auto-cat"]`) but with DIFFERENT
 * queryFns — the hook used a simple `?pageSize=200` (capped at 200 rows) while
 * the dialog cursor-looped through all pages. React Query stores one cache
 * entry per key, so whichever observer triggered the fetch determined the
 * queryFn, leading to silently capped propagation.
 *
 * Fix: one key, one cursor-loop queryFn, consumed by both callers.
 */

import { useQuery } from "@tanstack/react-query"
import { api } from "@/lib/api"
import { fetchAllUncategorized, type UncategorizedResponse } from "./uncategorized-fetch"

export interface UseUncategorizedTransactionsOptions {
  /** Pass panel-open / dialog-open so the fetch only fires when needed. */
  enabled?: boolean
}

export function useUncategorizedTransactions(options: UseUncategorizedTransactionsOptions = {}) {
  const { enabled = true } = options

  const { data, isLoading } = useQuery<UncategorizedResponse>({
    queryKey: ["transactions", "uncategorized-all"],
    queryFn: async () => ({
      success: true as const,
      data: await fetchAllUncategorized((url) => api.get(url)),
    }),
    enabled,
  })

  return { rows: data?.data ?? [], isLoading }
}
